'use client';

import { useParams } from 'next/navigation';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { StrapiCountries } from '@/apis/strapi';
import { buildSearchOptions, buildStops, StopSearchObject } from '@/app/builders';
import { doTranslation } from '@/app/i18n/client';
import styles from '@/components/bus-station/bus-station-search.module.scss';
import Button from '@/components/global/buttons/button';
import Select from '@/components/global/forms/select';
import { getLocations } from '@/context/trip-search-context';
import { localeSpecificPath } from '@/utils/localeUtils';
import { FormError } from '@/utils/types';

interface Props extends React.HTMLAttributes<HTMLButtonElement> {
  testLocations?: StopSearchObject[];
  city?: string;
  stopSlug?: string | null;
}

const BusStationSearch = (props: Props) => {
  const { lng } = useParams() as { lng: string };
  const { t } = doTranslation(lng);
  const busStationRef = useRef(null);

  const [busStationValue, setBusStationValue] = useState(props.stopSlug ? props.stopSlug : props.city ?? '');
  const [busStationInput, setBusStationInput] = useState('');
  const [busStationError, setBusStationError] = useState<FormError | undefined>(undefined);
  const [busStationOptions, setBusStationOptions] = useState<any[]>([]);
  const [locations, setLocations] = useState<StopSearchObject[]>([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    if (props.testLocations) {
      setLocations(props.testLocations);
      return;
    }
    (async () => {
      const locations = await getLocations(true);
      if (locations) {
        setLocations(buildStops(locations));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOptions = useCallback(
    async (query: string) => {
      if (query && query.length >= 2) {
        const data = buildSearchOptions(locations, query);
        if (data) {
          setBusStationOptions(data);
          return data;
        }
      }
      return [];
    },
    [locations]
  );

  useEffect(() => {
    const fetchOptions = async () => {
      if (busStationInput.length <= 1) {
        setBusStationOptions([]);
        return;
      }
      const options = await loadOptions(busStationInput);
      if (busStationInput && busStationInput.length >= 2 && options && options.length === 0) {
        setBusStationError({
          normal: t('No matches found.'),
          screenReader: t('We do not recognize the city you supplied.')
        });
      }
    };
    fetchOptions();
  }, [busStationInput, busStationValue, loadOptions, t, locations]);

  const handleSubmit = () => {
    let hasFocus = false;
    if (busStationValue.length === 0 || busStationError) {
      setBusStationError({
        normal: 'Please select a city',
        screenReader: t('We do not recognize the city you supplied.')
      });
      if (hasFocus === false) {
        if (busStationRef.current) {
          // @ts-ignore
          setTimeout(() => busStationRef.current?.focus());
        }
        hasFocus = true;
      }
      return;
    }
    setBusStationError(undefined);
    const u = new URL(window.location.origin);
    const urlParams = new URLSearchParams();
    u.pathname = localeSpecificPath(`/bus-station/${busStationValue}/`, lng);
    u.search = urlParams.toString();
    window.location.href = u.toString();
    return;
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (event.key === 'ArrowUp') {
        const labelTextElement = document.getElementById('label-text') as HTMLElement;
        labelTextElement.focus();
        return;
      }

      const focusableElement = document.getElementById('focusable-element') as HTMLElement;
      if (focusableElement) {
        focusableElement.focus();
      }
    }
  };

  return (
    <form
      className={styles['bus-station-search']}
      onSubmit={(event) => {
        event.preventDefault();
        handleSubmit();
      }}
      id="bus-station-search"
    >
      <div className={styles['station-search']}>
        <Select
          async
          autoComplete="off"
          autoHighlight={true}
          error={busStationError}
          inputValue={busStationInput || busStationValue || ''}
          label={t('Find Terminal')}
          mandatory
          name="bus-station"
          onChange={(location: any) => {
            setBusStationValue(location?.value || '');
            setBusStationError(undefined);
          }}
          onInputChange={(v: string) => {
            setBusStationInput(v);
          }}
          options={busStationOptions}
          placeholder={t('Choose a city')}
          reference={busStationRef}
          value={
            (busStationValue && locations.find((location: any) => location.value === busStationValue)?.label) || ''
          }
        />
        <span className="sr-only" id="bus-station-instructions">
          {t(
            'Start typing a city to open location options, and then use the arrow keys to navigate to the city you want, then press enter to select that city'
          )}
        </span>
        <div className={styles.button}>
          <Button primary={true} type="submit" id="focusable-element">
            {t('Search')}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default BusStationSearch;
